import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
  Activity,
  ActivityStudent,
  ActivityStudentAnswers,
  Answers,
  Exercise,
  InputAnswer,
  StudentMultiAnswers,
  Tags,
} from '@tekman/onmat/models';
import { UserService } from '@tekman/onmat/service/generic';
import { Category } from '@tekman/onmat/service/onmat';
import { GeogebraService, MathService, RadioButtonsService, TekareaService, TekinputService } from '@tekman/shared/component/generic';
import { Subscription } from 'rxjs';
import { Page } from '../../models/pagination.model';
import { StatementEventsService } from '../../service/statement-events.service';
import { RenderStatementComponent } from './render-statement.component';

import html2canvas from 'html2canvas';
import jspdf from 'jspdf';

@Component({
  selector: 'activity-statement',
  templateUrl: './statement.component.html',
  styleUrls: ['./statement.component.scss'],
})
export class StatementComponent implements OnInit, OnChanges, OnDestroy {
  @Input() activity: Activity;
  @Input() exercise: Exercise;
  @Input() categoryId: string;
  @Input() studentAnswers: ActivityStudentAnswers[] = [];
  @Input() isSent: boolean;
  @Input() status: number;
  @Input() tags: Tags[];
  @Input() printActivity: string;
  @Input() currentPage: Page;
  @Input() isResultPage = false;
  @Input() print = false;
  @Input() isTry: boolean;

  @ViewChild('statement', { static: false }) statement: RenderStatementComponent;
  isTeacher: boolean;
  validate = false;
  htmlStatement = '';
  subscription: Subscription;
  private allStudentAnswers: ActivityStudentAnswers[];
  private currentStudentAnswers: ActivityStudentAnswers;
  private answers: Answers[] = [];

  constructor(
    private readonly tekinputService: TekinputService,
    private readonly geogebraService: GeogebraService,
    private readonly tekareaService: TekareaService,
    private readonly mathService: MathService,
    private readonly radiobuttonService: RadioButtonsService,
    private readonly statementEvents: StatementEventsService,
    private readonly userService: UserService
  ) {}

  ngOnInit() {
    this.isTeacher = this.userService.isTeacher();
    this.listenEvents();
  }

  private listenEvents(): void {
    this.subscription = this.statementEvents.sendActivity$?.subscribe((send: boolean) => {
      this.validate = send;
      if (send) {
        this.send();
      }
    });
    this.subscription.add(
      this.statementEvents.saveActivity$.subscribe(() => {
        this.save();
      })
    );
  }

  ngOnChanges(change: SimpleChanges): void {
    this.isTeacher = this.userService.isTeacher();
    if (
      (change['studentAnswers']?.currentValue?.length > 0 && (!this.isTeacher || (this.isTeacher && this.isSent))) ||
      change['printActivity']?.currentValue ||
      change['isSent'] ||
      change['exercise']
    ) {
      this.setStatement(change);
    }
  }

  private setStatement(change: SimpleChanges): void {
    let statementHtml = '';
    if (this.exercise) {
      statementHtml = this.exercise.statement;
    }
    if (
      change['studentAnswers']?.currentValue !== undefined &&
      change['studentAnswers']?.currentValue?.length > 0 &&
      (!this.isTeacher || (this.isTeacher && this.isSent))
    ) {
      this.allStudentAnswers = change['studentAnswers'].currentValue;
      this.currentStudentAnswers = change['studentAnswers'].currentValue.filter(exercise => exercise.exerciseId === this.exercise.id)[0];
      this.validate = this.currentStudentAnswers?.sentAt && change['currentPage']?.currentValue?.isLocked ? true : this.isSent;
    }
    if (
      change['studentAnswers']?.currentValue?.length === 0 ||
      (change['isSent'] && !change['isSent']?.currentValue && change['isSent']?.previousValue)
    ) {
      this.currentStudentAnswers = null;
    }
    if (change['isSent']) {
      this.validate = this.currentStudentAnswers
        ? change['currentPage']?.currentValue?.isLocked
          ? true
          : change['isSent'].currentValue
        : false;
    }
    if (change['exercise']?.currentValue !== undefined) {
      this.answers = change['exercise'].currentValue.answers;
      this.currentStudentAnswers = this.allStudentAnswers?.filter(
        exercise => exercise.exerciseId === change['exercise'].currentValue.id
      )[0];
      statementHtml = this.exercise.statement;
    }
    if (change['printActivity']?.currentValue) {
      statementHtml = change['printActivity'].currentValue;
    }
    this.parseHtml(this.currentStudentAnswers, this.answers, statementHtml);
  }

  private async parseHtml(studentAnswers: ActivityStudentAnswers, answers: Answers[], statementHtml) {
    if (this.categoryId !== Category.RECURSOS) {
      statementHtml = this.tekinputService.parseTekinput(
        statementHtml,
        this.isTeacher,
        studentAnswers,
        this.validate,
        this.tags,
        this.status,
        this.isSent || !this.isResultPage,
        this.isTry
      );
      statementHtml = this.tekareaService.parseTekarea(statementHtml, this.isTeacher, studentAnswers, this.validate, this.categoryId);
      statementHtml = this.radiobuttonService.parseRadioButton(
        statementHtml,
        this.isTeacher,
        answers,
        studentAnswers,
        this.validate,
        this.isSent || !this.isResultPage
      );
    }

    statementHtml = this.geogebraService.getGeogebraId(statementHtml);
    statementHtml = this.mathService.parseMath(statementHtml);
    this.htmlStatement = statementHtml;
  }

  public convertToPDF() {
    if (this.print) {
      setTimeout(() => {
        const data = document.getElementsByName('convertToPdf')[0];
        html2canvas(data, {
          allowTaint: false,
          useCORS: true,
        }).then(canvas => {
          const imgWidth = 208;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          const contentDataURL = canvas.toDataURL('image/png', 1.0);
          const pdf = new jspdf('p', 'mm', 'a4');
          const position = 0;
          pdf.addImage(contentDataURL, 'PNG', 0, position, imgWidth, imgHeight);
          pdf.save(this.exercise.id + '.pdf');
        });
      }, 1000);
    }
  }

  private saveStatement(): ActivityStudent {
    const inputs: InputAnswer[] = [];
    let multiple: StudentMultiAnswers[] = [];
    const radiobuttons = document.getElementsByName('tekradio') as NodeListOf<HTMLInputElement>;
    this.statement.factories.forEach(factory => {
      if (factory.factory.selector === 'tekman-math' || factory.factory.selector === 'geogebra') {
        return;
      }
      if (factory.factory.selector === 'radio-buttons') {
        radiobuttons.forEach(radio => {
          factory.embeddedComponents.forEach(element => {
            if (element.instance.id === radio.id) {
              multiple.push({ answerId: radio.id, selected: radio.checked });
            }
          });
        });
        return;
      } else {
        factory.embeddedComponents.forEach(element => {
          inputs.push({ order: element.instance.order, value: element.instance.value });
        });
      }
      multiple = [];
    });
    const studentAnswers: ActivityStudentAnswers = {
      exerciseId: this.exercise?.id,
      inputs: inputs,
      multipleAnswers: multiple,
    };
    return { activity: this.activity, studentAnswers: studentAnswers };
  }

  save() {
    const activityStudent: ActivityStudent = this.saveStatement();
    this.statementEvents.emitSaveInputs(activityStudent);
  }

  send() {
    const activityStudent: ActivityStudent = this.saveStatement();
    this.statementEvents.emitSendInputs(activityStudent);
  }

  ngOnDestroy() {
    this.subscription && this.subscription.unsubscribe();
  }

  onInputChange() {
    const activityStudent: ActivityStudent = this.saveStatement();
    this.statementEvents.emitInputChange(activityStudent.studentAnswers);
  }
}
